import FormPublicWrapper from "@/components/form-public-wrapper";
import {
  PublicAuthCard,
  PublicAuthFooterLink,
  PublicAuthSubmitButton,
  publicAuthInputClassName,
  publicAuthLabelClassName,
} from "@/components/public-auth-card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import Routes from "@/routes/paths";
import { UserService } from "@/services/api/user";
import { getToastErrorContent } from "@/utils/functions/api-error";
import { zodResolver } from "@hookform/resolvers/zod";
import { Mail } from "lucide-react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import * as z from "zod";

const FormSchema = z.object({
  email: z
    .string()
    .min(1, { message: "Campo obrigatório" })
    .email({ message: "E-mail inválido" }),
});

type FormSchemaType = z.infer<typeof FormSchema>;

export default function SendLinkEmailScreen() {
  const { toast } = useToast();
  const navigate = useNavigate();

  const form = useForm<FormSchemaType>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      email: "",
    },
  });

  async function onSubmit(data: FormSchemaType) {
    try {
      const { email } = FormSchema.parse(data);

      await UserService.forgotPassword(email);

      toast({
        variant: "success",
        description: `Um e-mail contendo as instruções foi enviado para ${email}.`,
      });

      navigate(Routes.base, { replace: true });
    } catch (error) {
      toast(getToastErrorContent(error));
    }
  }

  const { isSubmitting } = form.formState;

  return (
    <FormPublicWrapper variant="modern">
      <PublicAuthCard
        title="Recuperar senha"
        description="Informe seu e-mail e enviaremos as instruções para redefinir sua senha"
        footer={
          <PublicAuthFooterLink
            text="Lembrou sua senha?"
            linkText="Voltar ao login"
            to={Routes.base}
          />
        }
      >
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className={publicAuthLabelClassName}>
                    E-mail
                  </FormLabel>

                  <FormControl>
                    <div className="relative">
                      <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                      <Input
                        placeholder="seu@email.com"
                        autoComplete="email"
                        className={publicAuthInputClassName}
                        {...field}
                      />
                    </div>
                  </FormControl>

                  <FormMessage className="text-red-400" />
                </FormItem>
              )}
            />

            <PublicAuthSubmitButton
              isSubmitting={isSubmitting}
              loadingText="Enviando..."
            >
              Enviar instruções
            </PublicAuthSubmitButton>
          </form>
        </Form>
      </PublicAuthCard>
    </FormPublicWrapper>
  );
}
